import { FC, useEffect, useRef } from "react"
import { Formik, FormikProps, Field, ErrorMessage } from "formik"
import { object, string } from "yup"
import SidePanel from "../../side-panel"
import { Channel, ChannelFormValues, ChannelStatus } from "../../../../types"
import { useCreateOrUpdateChannelMutation } from "../../../../redux/queries"
import { useAppDispatch } from "../../../hooks"
import { pushToast } from "../../../../redux/slices/notifications"
import Select from "../../form/select"
import { Copy, Key } from "lucide-react"

interface Props {
  open: boolean
  data?: Channel
  orgId: string
  onClose: () => void
}

const ChannelSchema = object({
  name: string().required("Name is required").min(1, "Name is required"),
  cpl_dollars: string()
    .required("CPL is required")
    .test("is-valid-amount", "Must be a valid dollar amount", value => {
      if (!value) return false
      const num = parseFloat(value)
      return !isNaN(num) && num >= 0
    }),
  status: string().required("Status is required"),
  description: string().max(500, "Description must be 500 characters or less")
})

const statusOptions = [
  { id: "active", name: "Active" },
  { id: "paused", name: "Paused" }
]

const ManageChannel: FC<Props> = ({ open, data, orgId, onClose }) => {
  const dispatch = useAppDispatch()
  const formRef = useRef<FormikProps<ChannelFormValues>>(null)

  const [
    createOrUpdate,
    { data: response, isSuccess, isError, reset: resetResponse }
  ] = useCreateOrUpdateChannelMutation()

  const isCreate = !data?.id

  useEffect(() => {
    if (!open) {
      formRef.current?.resetForm()
    }
  }, [open])

  const initialValues: ChannelFormValues = data
    ? {
        id: data.id,
        org_id: data.org_id,
        name: data.name,
        cpl_dollars: data.cpl_dollars,
        status: data.status,
        description: data.details?.description ?? ""
      }
    : {
        org_id: orgId,
        name: "",
        cpl_dollars: "",
        status: "active" as ChannelStatus,
        description: ""
      }

  const onSubmit = () => {
    if (formRef.current?.values) {
      const values = formRef.current.values
      const payload: any = {
        org_id: values.org_id,
        name: values.name,
        cpl_cents: Math.round(parseFloat(values.cpl_dollars) * 100),
        status: values.status,
        details: {
          description: values.description
        }
      }

      if (values.id) {
        payload.id = values.id
      }

      createOrUpdate(payload)
    }
  }

  useEffect(() => {
    if (isSuccess && response?.status === "success") {
      dispatch(
        pushToast({
          msg: `Successfully ${isCreate ? "created" : "updated"} channel`,
          theme: "success",
          timeout: 3000
        })
      )

      if (isCreate) {
        resetResponse()
        formRef.current?.resetForm()
        onClose()
      } else {
        if (formRef.current) {
          formRef.current.resetForm({ values: formRef.current.values })
        }
        resetResponse()
      }
    }
  }, [dispatch, isCreate, isSuccess, onClose, resetResponse, response])

  useEffect(() => {
    if (isError || response?.status === "error") {
      dispatch(
        pushToast({
          msg:
            response?.message ||
            `Failed to ${isCreate ? "create" : "update"} channel`,
          theme: "danger",
          timeout: 3000
        })
      )
      resetResponse()
    }
  }, [dispatch, isCreate, isError, resetResponse, response])

  const copyChannelKey = () => {
    if (data?.channel_key) {
      navigator.clipboard.writeText(data.channel_key)
      dispatch(
        pushToast({
          msg: "Channel key copied to clipboard",
          theme: "success",
          timeout: 2000
        })
      )
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      innerRef={formRef}
      validationSchema={ChannelSchema}
      enableReinitialize
    >
      {({ values, isValid, dirty, setFieldValue }) => {
        const hasChanges = dirty

        return (
          <SidePanel
            title={isCreate ? "Create Channel" : `Edit Channel - ${data?.name}`}
            open={open}
            onClose={onClose}
            actions={[
              {
                label: isCreate ? "Create Channel" : "Save Changes",
                type: "primary",
                onClick: onSubmit,
                disabled: !(isValid && hasChanges)
              }
            ]}
          >
            <div className="space-y-6 pt-2 sm:pt-4">
              {/* Channel Key (read-only for existing channels) */}
              {!isCreate && data?.channel_key && (
                <div className="bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Key size={16} className="text-slate-500" />
                    <label className="text-sm font-semibold text-slate-700">
                      Channel Key
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-white px-3 py-2 rounded-lg border border-slate-200 text-xs font-mono text-slate-600 truncate">
                      {data.channel_key}
                    </code>
                    <button
                      type="button"
                      onClick={copyChannelKey}
                      className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                      title="Copy channel key"
                    >
                      <Copy size={16} className="text-slate-500" />
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Use this key to authenticate lead submissions from your
                    affiliate partner.
                  </p>
                </div>
              )}

              {/* Name */}
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-semibold text-gray-700 mb-1"
                >
                  Channel Name <span className="text-red-400">*</span>
                </label>
                <Field
                  id="name"
                  name="name"
                  type="text"
                  placeholder="e.g., rates.now"
                  className="appearance-none block w-full px-4 py-3 border border-gray-200 rounded-xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                />
                <ErrorMessage
                  name="name"
                  component="p"
                  className="mt-1 text-sm text-red-600"
                />
              </div>

              {/* CPL */}
              <div>
                <label
                  htmlFor="cpl_dollars"
                  className="block text-sm font-semibold text-gray-700 mb-1"
                >
                  Cost Per Lead (CPL) <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                    $
                  </span>
                  <Field
                    id="cpl_dollars"
                    name="cpl_dollars"
                    type="text"
                    placeholder="5.00"
                    className="appearance-none block w-full pl-8 pr-4 py-3 border border-gray-200 rounded-xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                  />
                </div>
                <ErrorMessage
                  name="cpl_dollars"
                  component="p"
                  className="mt-1 text-sm text-red-600"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Amount charged per lead submitted through this channel
                </p>
              </div>

              {/* Status */}
              <div>
                <Select
                  label="Status"
                  data={statusOptions}
                  selected={values.status}
                  setSelected={(val: string) => setFieldValue("status", val)}
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label
                  htmlFor="description"
                  className="block text-sm font-semibold text-gray-700 mb-1"
                >
                  Description
                </label>
                <Field
                  as="textarea"
                  id="description"
                  name="description"
                  rows={3}
                  placeholder="Optional description for this channel..."
                  className="appearance-none block w-full px-4 py-3 border border-gray-200 rounded-xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none"
                />
              </div>
            </div>
          </SidePanel>
        )
      }}
    </Formik>
  )
}

export default ManageChannel
